import { IName } from "../interfaces/global.interfaces";
import { IOrderItem, IOrderItemModifier, IOrderGiftCard, IOrderItemCoupon, IOrderCoupon } from "../models/order.interface";
import {
    IDataLayer_Ecommerce_Item,
    IDataLayer_Ecommerce_View_Select_Promotion_Event, IDataLayer_Ecommerce_View_Item_List_Event,
    IDataLayer_Ecommerce_Select_View_Item_Event, IDataLayer_Ecommerce_View_Cart_Event, IDataLayer_Ecommerce_Remove_Cart_Event,
    IDataLayer_Ecommerce_Add_To_Cart_Event, IDataLayer_Ecommerce_Begin_Checkout_Event, IDataLayer_Ecommerce_Add_Shipping_Info_Event,
    IDataLayer_Ecommerce_Add_Payment_Info_Event, IDataLayaer_Ecommerce_Purchase_Event,
    IDataLayer_Page_View_Event, IDataLayer_Sign_Up_Event,
    IDataLayer_Sign_In_Event, IDataLayer_Update_Profile_Event, IDataLayer_Profile_Event,
    IDataLayer_User_Properties, IDataLayer_Select_Store_Event, IDataLayer_Menu_Click_Event,
    IDataLayer_Start_Order_Click_Event, IDataLayer_Add_More_Items_Event, IDataLayer_Customize_Item_Event,
    IDataLayer_Customize_Option_Selected_Event, IDataLayer_Modal_Close_Event, IDataLayer_Apply_Coupon_Event,
    IDataLayer_Upsell_Selected_Event, IDataLayer_Rewards_Redeem_Event, IDataLayer_Track_Order_Click_Event,
    IDataLayer_Track_Order_Connect_Event, IDataLayer_Re_Order_Click_Event, IDataLayer_Favorite_Event,
    IDataLayer_App_Download_Event, IDataLayer_Error_Message_Event
} from './data-layer-service.interface';
import { IUser } from '../models/user.interface';
import { IDataCouponItem, IDataCoupon } from '../models/data.interface';
import { PaymentType, PlatformType, LoginStatus, ContentType, ToppingType, OrderType } from "../types/enums";
import { Data } from "../models/data";
import { Order } from "../models/order";
import { Util } from "../utils/util";
import { Context } from "../models/context";
import { LocalizationService } from "./localization-service";

/**
 * Class to handle interactions with the Google Analytics dataLayer.
 */
export class DataLayerService {
    private readonly _data: Data;
    private readonly _context: Context;
    private readonly _localizationService: LocalizationService;
    viewedItems: number[] = [];
    viewedCoupons: number[] = [];

    constructor(data: Data, context: Context, localizationService: LocalizationService) {
        this._data = data;
        this._context = context;
        this._localizationService = localizationService;
    }

    /**
     * Fires when a user views a promotion from the landing page or the menu meal deal page and includes:
     * * - event, creative_name, creative_slot, index, promotion_id, promotion_name, items
     * @param {number[]} couponIds - The IDs of the coupons to be viewed.
     * @param {string} promoLocation - The location of the promotion (e.g., "Menu Promotion", "Home Promotion"). (optional)
     * @param {number} index - The index of the promotion in the list. (optional)
     * @param {string} couponName - The name of the coupon (optional and passed when sending from brand-level data).
     * @returns
     */
    public view_promotion(couponIds: number[], promoLocation?: string, idIndexes?: [{ id: number, idx: number }], couponName?: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        let items: IDataLayer_Ecommerce_Item[] = [];
        let dataLayerObject: IDataLayer_Ecommerce_View_Select_Promotion_Event;
        if (couponIds.length > 1) {
            for (const couponId of couponIds) {
                this.view_promotion([couponId], promoLocation, idIndexes, couponName);
            }
            return;
        }

        const couponId = couponIds[0];
        const index = idIndexes?.find(x => x.id === couponId)?.idx ?? 0;
        let id = '';
        let name = '';
        let couponItems = [];

        // Skip if the coupon has already been viewed
        if (this.viewedCoupons.includes(couponId))
            return;
        else
            this.viewedCoupons.push(couponId);

        [id, name, couponItems] = this._getCouponData(couponId, couponName);
        if (!id || !name)
            return; // Skip if no valid coupon data is found

        const creativeSlot = `${promoLocation} - Tile ${index}`;
        items = this._getCouponItems(couponItems);

        if (id && name) {
            const appOrMobile = Util.isAppView() ? 'app' : 'mobile';
            dataLayerObject = {
                event: "view_promotion",
                creative_name: `${promoLocation}_${name}_${appOrMobile}`,
                creative_slot: creativeSlot,
                index: index ?? 0,
                promotion_id: id,
                promotion_name: name,
                items: items
            };
        }

        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Gets the coupon data based on the coupon ID and optional coupon name. If a store is selected, it gets the coupon data from the store's properties.
     * If the store is not selected, it checks if the coupon ID and name are provided and returns them.
     * @param couponId - The ID of the coupon.
     * @param couponName - The name of the coupon (optional and passed when sending from brand-level data).
     * @returns {[string, string, IDataCouponItem[]]} - An array containing the coupon ID, coupon name, and an array of coupon items.
     */
    private _getCouponData(couponId: number, couponName?: string): [string, string, IDataCouponItem[]] {
        if (this._data.storeKey) {
            const coupon = this._data.getProperty("Coupons").find(x => x.CouponId === couponId);
            if (!coupon)
                return ['', '', []];

            return [coupon.CouponId.toString(), this._localizationService.GetName(coupon.Names), coupon.Items];
        } else if (couponId && couponName) {
            return [couponId.toString(), couponName, []];
        }
        return ['', '', []];
    }

    /**
     * Converts the coupon items array to an Ecommerce item for the datalayer.
     * @param {IDataCouponItem[] | null} couponItems - The array of coupon items to be converted.
     * @param {string | null} couponCode - The code of the coupon, if applicable (optional).
     * @returns {IDataLayer_Ecommerce_Item[]} - An array of Ecommerce items for the datalayer.
     */
    private _getCouponItems(couponItems: IDataCouponItem[] | null, couponCode?: string | null): IDataLayer_Ecommerce_Item[] {
        const items: IDataLayer_Ecommerce_Item[] = [];

        if (!couponItems || couponItems.length === 0)
            return items;

        for (const couponItem of couponItems) {
            const mItem = this._data.getProperty("Items").find(x => x.ItemId === couponItem.ItemId);

            const item: IDataLayer_Ecommerce_Item = {
                item_id: couponItem.ItemId.toString(),
                item_name: mItem ? this._localizationService.GetName(mItem.Names) : "",
                index: couponItem.ItemIndex,
            }

            if(couponCode)
                item.coupon = couponCode;

            items.push(item);
        }
        return items;
    }

    /**
     * Select_promotion: fires when a user clicks on a specific promotion and includes: 
     *  - promotion id, promotion name, creative name, creative slot, location id, items
     * @param {number} couponId - The ID of the coupon being selected.
     * @param {IName[]} couponNames - The names of the coupon in different languages.
     * @param {string} promoLocation - The location of the promotion (e.g., "Menu Promotion", "Home Promotion").
     * @param {number} promoIndex - The index of the promotion in the list.
     * @param {string | null} couponCode - The code of the coupon, if applicable (optional).
     */
    public select_promotion(couponId: number, couponNames: IName[], promoLocation: string, promoIndex: number, couponCode?: string | null): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        let items: IDataLayer_Ecommerce_Item[] = [];
        const couponName = this._localizationService.GetName(couponNames);
        const appOrMobile = Util.isAppView() ? 'app' : 'mobile';


        let id = '';
        let name = '';
        let couponItems = [];

        [id, name, couponItems] = this._getCouponData(couponId, couponName);
        if (!id || !name)
            return; // Skip if no valid coupon data is found

        const creativeName = `${promoLocation}_${name}_${appOrMobile}`;
        const creativeSlot = `${promoLocation} - Tile ${promoIndex}`;
        items = this._getCouponItems(couponItems, couponCode);

        const dataLayerObject: IDataLayer_Ecommerce_View_Select_Promotion_Event = {
            event: "select_promotion",
            creative_name: creativeName,
            creative_slot: creativeSlot,
            index: promoIndex,
            promotion_id: couponId.toString(),
            promotion_name: name,
            items: items
        };

        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * view_item_list: fires whenever any product is displayed to a user and includes:  
     *  - currency, item_list_id, item_list_name, items, store_number
     * @param {number[]} itemIds - The IDs of the items to be viewed.
     * @param {string} itemListName - The name of the item list being viewed.
     */
    public view_item_list(itemIds: number[], itemListName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const items: IDataLayer_Ecommerce_Item[] = [];
        let categoryId = '';
        let categoryName = '';
        const currencyCode = this._getStoreCurrency();
        itemListName = this._context.getProperty("upsellSelected") ? 'upsell' : itemListName;
        for (const [index, itemId] of itemIds.entries()) {
            if (this.viewedItems.includes(itemId))
                continue;
            else
                this.viewedItems.push(itemId);

            const mItem = this._data.getProperty("Items").find(x => x.ItemId === itemId);

            if (!mItem)
                continue;

            const category = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId == mItem.WebCategoryId);
            const itemCategoryId = category?.ItemWebCategoryId.toString() || '0';
            const itemCategoryName = category ? this._localizationService.GetName(category.Names) : "";
            if (!categoryId)
                categoryId = itemCategoryId;
            if (!categoryName)
                categoryName = itemCategoryName;

            items.push({
                item_id: mItem.ItemId.toString(),
                item_name: this._localizationService.GetName(mItem.Names),
                item_list_id: itemCategoryId,
                item_list_name: `${itemListName}_${itemCategoryName}`,
                currency: currencyCode,
                index: index,
                item_category: itemCategoryName
            });
        }

        const dataLayerObject: IDataLayer_Ecommerce_View_Item_List_Event = {
            event: "view_item_list",
            currency: currencyCode,
            item_list_id: categoryId,
            item_list_name: `${itemListName}_${categoryName}`,
            items: items,
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        if (items.length > 0)
            //@ts-ignore
            globalThis.dataLayer.push(dataLayerObject);
    }

    public select_item(order: Order, orderItem: IOrderItem, index: number, itemListName: string, promotionId?: number): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const mItem = this._data.getProperty("Items").find(x => x.ItemId === orderItem.ItemId);

        if (!mItem)
            return;

        itemListName = this._context.getProperty("upsellSelected") ? 'upsell' : itemListName;
        const category = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId == mItem.WebCategoryId);
        const categoryId = category?.ItemWebCategoryId.toString() ?? '0';
        const itemCategoryName = category ? this._localizationService.GetName(category.Names) : "";

        const dataLayerObject: IDataLayer_Ecommerce_Select_View_Item_Event = {
            event: "select_item",
            currency: this._getStoreCurrency(),
            item_list_id: categoryId,
            item_list_name: `${itemListName}_${itemCategoryName}`,
            value: orderItem.Price * orderItem.Quantity,
            index: index,
            items: [],
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, [orderItem], itemListName, promotionId);

        dataLayerObject.items = items;

        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }


    /**
     * view_item: fires when a user views a specific product (after product click) and includes:  
     *  - currency, item_list_id, item_list_name, value, index, items, store_number
     * @param {Order} order - The order object containing the item being viewed.
     * @param {IOrderItem} orderItem - The order item being viewed.
     * @param {number} index - The index of the item in the list.
     * @param {string} itemListName - The name of the item list being viewed.
     */
    public view_item(order: Order, orderItem: IOrderItem, index: number, itemListName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const mItem = this._data.getProperty("Items").find(x => x.ItemId === orderItem.ItemId);

        if (!mItem)
            return;

        itemListName = this._context.getProperty("upsellSelected") ? 'upsell' : itemListName;
        const category = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId == mItem.WebCategoryId);
        const categoryId = category?.ItemWebCategoryId.toString() ?? '0';
        const itemCategoryName = category ? this._localizationService.GetName(category.Names) : "";

        const dataLayerObject: IDataLayer_Ecommerce_Select_View_Item_Event = {
            event: "view_item",
            currency: this._getStoreCurrency(),
            item_list_id: categoryId,
            item_list_name: `${itemListName}_${itemCategoryName}`,
            value: orderItem.Price * orderItem.Quantity,
            index: index,
            items: [],
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, [orderItem], itemListName);

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * view_cart: fires when a user views their cart and includes:
     * -currency, value, items, store_number
     * @param {Order} order - The order object containing cart details.
     * @returns
     */
    public view_cart(order: Order): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const dataLayerObject: IDataLayer_Ecommerce_View_Cart_Event = {
            event: "view_cart",
            currency: this._getStoreCurrency(),
            value: order.getProperty("SubTotal"),
            items: [],
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, order.getProperty("Items"));

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * add_to_cart: fires when a user adds an item to their cart and includes:
     *  - add_more_items_click, currency, value, index, items, store_number
     * @param {Order} order - The order object containing cart details.
     * @param {IOrderItems | undefined} - The order items that were added to the cart.
     */
    public add_to_cart(order: Order, orderItems: IOrderItem[] | undefined, listName: string, promotionId?: number): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        if (!orderItems)
            return;

        const mItem = this._data.getProperty("Items").find(x => x.ItemId === orderItems[0].ItemId);
        if (!mItem)
            return;

        const value = orderItems.reduce((sum, item) => sum + (item.Price * item.Quantity), 0);

        const dataLayerObject: IDataLayer_Ecommerce_Add_To_Cart_Event = {
            event: "add_to_cart",
            add_more_items_click: this._context.getProperty("addMoreItemsClicked") ?? false,
            currency: this._getStoreCurrency(),
            value: value,
            index: orderItems[0].Index,
            items: [],
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, orderItems, listName, promotionId);

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * remove_from_cart: fires when a user removes an item from their cart and includes:
     * -currency, value, index, items, store_number
     * @param {Order} order - The order object containing cart details.
     * @param {IOrderItem} orderItem - The order item that was removed from the cart.
     */
    public remove_from_cart(order: Order, orderItem: IOrderItem): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const dataLayerObject: IDataLayer_Ecommerce_Remove_Cart_Event = {
            event: "remove_from_cart",
            currency: this._getStoreCurrency(),
            value: orderItem.Price * orderItem.Quantity,
            index: orderItem.Index,
            items: [],
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, [orderItem]);

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * begin_checkout: fires when the Checkout screen is opened and includes:
     * -currency, value, items, payment_type, order_total, coupon, store_number
     * @param {Order} order - The order items associated with the checkout.
     */
    public begin_checkout(order: Order): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const dataLayerObject: IDataLayer_Ecommerce_Begin_Checkout_Event = {
            event: "begin_checkout",
            currency: this._getStoreCurrency(),
            value: order.getProperty("SubTotal"),
            items: [],
            payment_type: this._getPaymentTypeName(order.getProperty("GiftCards"), order.getProperty("PaymentType")),
            order_total: order.getProperty("Total"),
            coupon: this._getCouponsNames(order),
            store_number: this._data.getProperty("StoreInfo").storeCode.toString()
        };

        const items = this._get_checkout_items(order, order.getProperty("Items"));

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * add_shipping_info: fires when shipping info is added or confirmed during checkout
     * @param {Order} order - The order associated with the shipping.
     * @param {boolean} isCurbside - Indicates if the order is for curbside pickup.
     */
    public add_shipping_info(order: Order, isCurbside: boolean): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const orderType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === order.getProperty("OrderTypeID"));
        const selectedTime = this._formatDate(order.getProperty("FutureDate"), order.getProperty("FutureMinute"));

        const dataLayerObject: IDataLayer_Ecommerce_Add_Shipping_Info_Event = {
            event: "add_shipping_info",
            currency: this._getStoreCurrency(),
            value: order.getProperty("SubTotal"),
            coupon: this._getCouponsNames(order),
            shipping_tier: orderType ? this._localizationService.GetName(orderType.Names) : "",
            selected_time: selectedTime,
            order_type: this._getOrderTypeName(orderType?.OrderTypeId, isCurbside),
            store_number: this._data.getProperty("StoreInfo").storeCode.toString(),
            items: []
        };

        const items = this._get_checkout_items(order, order.getProperty("Items"));

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * add_payment_info: fires when payment info is added or confirmed during checkout
     * @param {Order} order - The order associated with the payment.
     * @param {boolean} isCurbside - Indicates if the order is for curbside pickup.
     */
    public add_payment_info(order: Order, isCurbside: boolean): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const orderType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === order.getProperty("OrderTypeID"));
        const selectedTime = this._formatDate(order.getProperty("FutureDate"), order.getProperty("FutureMinute"));

        const dataLayerObject: IDataLayer_Ecommerce_Add_Payment_Info_Event = {
            event: "add_payment_info",
            currency: this._getStoreCurrency(),
            value: order.getProperty("SubTotal"),
            coupon: this._getCouponsNames(order),
            shipping_tier: orderType ? this._localizationService.GetName(orderType.Names) : "",
            selected_time: selectedTime,
            order_type: this._getOrderTypeName(orderType?.OrderTypeId, isCurbside),
            store_number: this._data.getProperty("StoreInfo").storeCode.toString(),
            payment_type: this._getPaymentTypeName(order.getProperty("GiftCards"), order.getProperty("PaymentType")),
            items: []
        };

        const items = this._get_checkout_items(order, order.getProperty("Items"));

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Formats the time based on the given date and time
     * @param {Date | null} date
     * @param {number} time
     * @returns
     */
    private _formatDate(date: Date | null, time: number): string {
        if (!date)
            return '';

        const finalDate = new Date(date.getTime() + (time * 60000));
        return finalDate.toString();
    }

    /**
     * purchase: fires when a user successfully places an order and includes:
     * -transaction_id, currency, value, shipping, tax, coupon, items, contactless_delivery, payment_type, order_total
     * shipping_tier, order_type, foundation_code, tip, donation, other_fees, est_delivery_mminutes, receive_notification, store_number
     * @param {Order} order - The order items associated with the purchase.
     * @param {number} orderId - The ID of the order that was purchased.
     * @param {boolean} isContactless - Indicates if the order is contactless.
     * @param {boolean} isCurbside - Indicates if the order is for curbside pickup.
     * @param {string} notificationType - Indicates if the user wants to receive notifications via SMS or email or both.
     */
    public purchase(order: Order, orderId: number, isContactless: boolean, isCurbside: boolean, notificationType: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const orderType = this._data.getProperty("OrderTypeSubTypes").find(x => x.OrderTypeId === order.getProperty("OrderTypeID"));
        const fundraiser = this._data.getProperty("Fundraisers").find(x => x.FundraiserId === order.getProperty("FundraiserID"));

        const dataLayerObject: IDataLayaer_Ecommerce_Purchase_Event = {
            event: "purchase",
            transaction_id: orderId.toString(),
            currency: this._getStoreCurrency(),
            value: order.getProperty("SubTotal"),
            shipping: order.getProperty("OrderTypeCharge"),
            tax: order.getProperty("Tax"),
            coupon: this._getCouponsNames(order),
            items: [],
            contactless_delivery: isContactless,
            payment_type: this._getPaymentTypeName(order.getProperty("GiftCards"), order.getProperty("PaymentType")),
            order_total: order.getProperty("Total"),
            shipping_tier: orderType ? this._localizationService.GetName(orderType.Names) : "",
            order_type: this._getOrderTypeName(orderType?.OrderTypeId, isCurbside),
            foundation_code: fundraiser?.Code ?? '',
            tip: order.getProperty("Tip"),
            donation: order.getProperty("Donations").Detail.reduce((acc, donation) => acc + donation.Amount, 0),
            other_fees: order.getProperty("ServiceCharges").Amount,
            est_delivery_minutes: order.getProperty("WaitTime"),
            receive_notification: notificationType,
            store_number: this._data.getProperty("StoreInfo").storeCode.toString(),
        };

        const items = this._get_checkout_items(order, order.getProperty("Items"));

        dataLayerObject.items = items;
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Gets the coupon names for an order.
     * @param {Order} order - The order object containing the coupons.
     * @returns {string} - A string containing the names of the coupons, separated by commas.
     */
    private _getCouponsNames(order: Order): string {
        const couponNames: string[] = [];
        for (const c of order.getProperty("Coupons")) {
            const coupon = this._data.getProperty("Coupons").find(x => x.CouponId == c.CouponId);
            if (coupon) {
                couponNames.push(this._localizationService.GetName(coupon.Names));
            }
        }
        return couponNames.join(', ');
    }

    /**
     * Gets checkout items for the dataLayer.
     * @param {Order} order - The order object containing the items to be checked out.
     * @param {IOrderItem[]} items - The items grouped by item key to be checked out.
     * @param {string} itemListName - The name of the item list. (optional)
     * @returns {IDataLayer_Checkout_Item[]} - The checkout items for the dataLayer.
     */
    private _get_checkout_items(
        order: Order,
        items: IOrderItem[],
        itemListName?: string,
        promotionId?: number
    ): IDataLayer_Ecommerce_Item[] {
        const grouped = this._groupItemsByKey(items);
        const currency = this._getStoreCurrency();

        return grouped.map(group => this._buildDataLayerItem(group, order, currency, itemListName, promotionId));
    }

    /**
     * Groups the order items by item key
     * @param {IOrderItem[]} items - The items grouped by item key to be checkd out
     * @returns {{ itemKey: number; itemHalves: IOrderItem[] }[]} - The grouped order items
     */
    private _groupItemsByKey(items: IOrderItem[]): { itemKey: number; itemHalves: IOrderItem[] }[] {
        const map = new Map<number, IOrderItem[]>();

        for (const item of items) {
            if (!map.has(item.ItemKey)) {
                map.set(item.ItemKey, []);
            }
            map.get(item.ItemKey)!.push(item);
        }

        return Array.from(map.entries()).map(([itemKey, itemHalves]) => ({ itemKey, itemHalves }));
    }

    /**
     * Gets the current store's currency code
     * @returns {string} - the currency code of the store
     */
    private _getStoreCurrency(): string {
        const storeCountryId = this._data.getProperty("StoreInfo").countryId;
        return this._data.getProperty("Countries").find(c => c.CountryID === storeCountryId)?.CurrencyCode ?? '';
    }

    /**
     * Builds a data layer item for an ecommerce event
     * @param group - Grouped order items by item key
     * @param order - The order object containing the items to be checked out.
     * @param currency - The currently selected store's currency code
     * @param itemListName - The name of the item list. (optional)
     * @returns {IDataLayer_Ecommerce_Item} - A checkout item for the dataLayer.
     */
    private _buildDataLayerItem(
        group: { itemKey: number; itemHalves: IOrderItem[] },
        order: Order,
        currency: string,
        itemListName?: string,
        promotionId?: number
    ): IDataLayer_Ecommerce_Item {
        const [firstHalf] = group.itemHalves;

        const dataLayerItem: IDataLayer_Ecommerce_Item = {
            item_id: firstHalf.ItemId.toString(),
            currency: currency,
            index: group.itemKey,
            item_category: "",
            item_category2: "",
            item_category3: "",
            item_category4: "",
            item_category5: "",
            price: 0,
            quantity: firstHalf.Quantity
        };

        for (const half of group.itemHalves) {
            dataLayerItem.price = (dataLayerItem.price ?? 0) + half.Price;

            if (half.HalfIndex < 2) {
                this._processItemData(half, dataLayerItem, itemListName);
                this._processCategories(half, dataLayerItem);
            }

            this._processModifiers(half.Modifiers, dataLayerItem);
            this._processDiscounts(order, half, dataLayerItem);
            this._processPromotion(promotionId, dataLayerItem);
        }

        return dataLayerItem;
    }

    /**
     * Processes the item name, item list id, and item list name.
     * @param {IOrderItem} item - The order item to be processed.
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem -  The dataLayer item to be updated.
     * @param {string} itemListName - The name of the item list (optional)
     */
    private _processItemData(item: IOrderItem, dataLayerItem: IDataLayer_Ecommerce_Item, itemListName?: string): void {
        const dataItem = this._data.getProperty("Items").find(x => x.ItemId == item.ItemId);
        dataLayerItem.item_name = dataItem ? this._localizationService.GetName(dataItem.Names) : "";

        const category = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId == dataItem?.WebCategoryId);
        const categoryId = category?.ItemWebCategoryId.toString() ?? '0';
        const itemCategoryName = category ? this._localizationService.GetName(category.Names) : "";
        const finalItemListName = itemListName ? `${itemListName}_${itemCategoryName}` : itemCategoryName;
        dataLayerItem.item_list_id = categoryId;
        dataLayerItem.item_list_name = finalItemListName;
    }

    /**
     * Processes the item categories for an order item and updates the dataLayer item.
     * @param {IOrderItem} item - The order item to be processed.
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     */
    private _processCategories(item: IOrderItem, dataLayerItem: IDataLayer_Ecommerce_Item): void {
        const dataItem = this._data.getProperty("Items").find(x => x.ItemId == item.ItemId);
        const category = this._data.getProperty("Categories").find(x => x.ItemWebCategoryId == dataItem?.WebCategoryId);
        const subCategory = this._data.getProperty("SubCategories").find(x => x.ItemSubCategoryId == dataItem?.SubCategoryId);
        const size = this._data.getProperty("Sizes").find(x => x.SizeId == item.SizeId);

        dataLayerItem.item_category = category ? this._localizationService.GetName(category.Names) : "";
        dataLayerItem.item_category2 = item.IsBuildYourOwn ? 'Build Your Own' : '';
        dataLayerItem.item_category3 = subCategory ? this._localizationService.GetName(subCategory.Names) : "";
        dataLayerItem.item_category4 = size ? this._localizationService.GetName(size.Names) : "";
    }

    /**
     * Processes the modifiers for an order item and updates the dataLayer item.
     * @param {IOrderItemModifier[]} modifiers - The modifiers for the order item.
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     */
    private _processModifiers(modifiers: IOrderItemModifier[], dataLayerItem: IDataLayer_Ecommerce_Item): void {
        const addOns: string[] = [];
        let addOnsPrice = 0;

        const toppingMap = Object.fromEntries(
            Object.values(ToppingType).map(type => [type, { arr: [] as string[] }])
        ) as Record<string, { arr: string[] }>;

        for (const mod of modifiers) {
            addOnsPrice += this._processSingleModifier(mod, addOns, toppingMap, dataLayerItem);
        }

        if (addOns.length > 0) {
            dataLayerItem.add_ons = this._appendAddOns(dataLayerItem.add_ons, addOns);
            dataLayerItem.add_ons_price = (dataLayerItem.add_ons_price ?? 0) + addOnsPrice;
        }

        this._setDataLayerToppingLists(dataLayerItem, toppingMap);
    }

    /**
     * Helper function to concatonate names for addons
     * @param {string | undefined} existing - The string of addons names if they exist
     * @param {string[]} newAddOns - The new string of addons names to add to the existing
     * @returns {string} - The new concatenated string of all addons names
     */
    private _appendAddOns(existing: string | undefined, newAddOns: string[]): string {
        return existing ? `${existing}, ${newAddOns.join(', ')}` : newAddOns.join(', ');
    }

    /**
     * Processes a single modifier for an order item and updates the add-ons, item category 5, and topping map.
     * @param {IOrderItemModifier} mod - The modifier to be processed.
     * @param {string[]} addOns - The array to store add-on names.
     * @param { [key: string]: { arr: string[] } } toppingMap - The map to store toppings categorized by type.
     * @returns
     */
    private _processSingleModifier(
        mod: IOrderItemModifier,
        addOns: string[],
        toppingMap: { [key: string]: { arr: string[] } },
        dataLayerItem: IDataLayer_Ecommerce_Item
    ): number {
        const mMod = this._data.getProperty("Modifiers").find(x => x.ModifierId == mod.ModifierId);
        if (!mMod) return 0;

        let priceToAdd = 0;

        const modName = this._localizationService.GetName(mMod.Names);
        if (!mod.IsDefault && !dataLayerItem?.add_ons?.includes(modName)) {
            addOns.push(modName);
            priceToAdd += mod.Price;
        } else if (mod.IsDefault && !dataLayerItem.item_category5?.includes(modName)) {
            dataLayerItem.item_category5 += `${modName}, `;
        }

        for (const type in toppingMap) {
            const topping = this._getMatchingToppingByType(type, mod.ModifierId);
            if (topping) {
                toppingMap[type].arr.push(topping);
            }
        }

        return priceToAdd;
    }

    /**
     * Gets the matching topping by type for a given modifier ID.
     * @param {string} type - The type of topping (e.g., meat, veg, cheese, crust, sauce).
     * @param {number} modifierId - The ID of the modifier to check.
     * @returns
     */
    private _getMatchingToppingByType(type: string, modifierId: number): string {
        const modifierCategories = this._data.getProperty("ModifierCategories");
        const modifiers = this._data.getProperty("Modifiers");

        const modifier = modifiers.find(x => x.ModifierId == modifierId);
        const modifierCategory = modifierCategories.find(x => x.ModifierWebCategoryId === Number(modifier?.ModifierWebCategoryId));
        if (!modifier || !modifierCategory)
            return '';

        const name = this._localizationService.GetName(modifierCategory.Names);
        if (name.toLowerCase().includes(type)) {
            const modName = this._localizationService.GetName(modifier.Names);
            return modName;
        }

        return '';
    }

    /**
     * Sets the topping lists in the dataLayer item based on the topping map.
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     * @param { [key: string]: { arr: string[] } } toppingMap - The map containing arrays of toppings categorized by type.
     */
    private _setDataLayerToppingLists(dataLayerItem: IDataLayer_Ecommerce_Item, toppingMap: { [key: string]: { arr: string[] } }): void {
        if (toppingMap[ToppingType.MEAT].arr.length > 0) dataLayerItem.topping_list_meat = toppingMap[ToppingType.MEAT].arr.join(', ');
        if (toppingMap[ToppingType.VEG].arr.length > 0) dataLayerItem.topping_list_veggies = toppingMap[ToppingType.VEG].arr.join(', ');
        if (toppingMap[ToppingType.CHEESE].arr.length > 0) dataLayerItem.topping_list_cheese = toppingMap[ToppingType.CHEESE].arr.join(', ');
        if (toppingMap[ToppingType.CRUST].arr.length > 0) dataLayerItem.topping_list_crust = toppingMap[ToppingType.CRUST].arr.join(', ');
        if (toppingMap[ToppingType.SAUCE].arr.length > 0) dataLayerItem.topping_list_sauce = toppingMap[ToppingType.SAUCE].arr.join(', ');
    }

    /**
     * Processes the discounts for an order item and updates the dataLayer item.
     * @param {Order} order - The order object.
     * @param {IOrderItem} item - The order item.
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     */
    private _processDiscounts(order: Order, item: IOrderItem, dataLayerItem: IDataLayer_Ecommerce_Item): void {
        const itemDiscounts = order.getProperty("ItemsCoupons").filter(x => x.ItemKey === item.ItemKey && x.HalfIndex === 1);
        if (!itemDiscounts.length) return;

        const orderCoupons = order.getProperty("Coupons");
        const allCoupons = this._data.getProperty("Coupons");

        for (const itemDiscount of itemDiscounts) {
            this._applyDiscount(dataLayerItem, itemDiscount);
            this._appendCouponName(dataLayerItem, itemDiscount, orderCoupons, allCoupons);
        }
    }

    /**
     * Applies discount information to the datalayer item
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     * @param {IOrderItemCoupon} itemDiscount - the order item coupon containing discount information for an item
     */
    private _applyDiscount(dataLayerItem: IDataLayer_Ecommerce_Item, itemDiscount: IOrderItemCoupon): void {
        const discountAmount = itemDiscount.AfterTaxDiscount + itemDiscount.BeforeTaxDiscount;
        dataLayerItem.discount = (dataLayerItem.discount ?? 0) + discountAmount;
    }

    /**
     * Updates the coupon name information for the datalayer item
     * @param {IDataLayer_Ecommerce_Item} dataLayerItem - The dataLayer item to be updated.
     * @param {IOrderItemCoupon} itemDiscount - All the order item coupons associated to an order
     * @param {IOrderCoupon[]} orderCoupons - All the coupons associated to an order
     * @param {IDataCoupon[]} allCoupons - All the coupons available for the selected store
     * @returns
     */
    private _appendCouponName(
        dataLayerItem: IDataLayer_Ecommerce_Item,
        itemDiscount: IOrderItemCoupon,
        orderCoupons: IOrderCoupon[],
        allCoupons: IDataCoupon[]
    ): void {
        const orderCoupon = orderCoupons.find(x => x.CouponKey === itemDiscount.CouponKey);
        if (!orderCoupon) return;

        const coupon = allCoupons.find(x => x.CouponId === orderCoupon.CouponId);
        const couponName = coupon ? this._localizationService.GetName(coupon.Names) : "";
        if (!couponName) return;

        dataLayerItem.coupon = dataLayerItem.coupon
            ? `${dataLayerItem.coupon}, ${couponName}`
            : couponName;
    }

    private _processPromotion(promotionId: number | undefined, dataLayerItem: IDataLayer_Ecommerce_Item): void {
        if (!promotionId)
            return;

        let id = '';
        let name = '';

        [id, name] = this._getCouponData(promotionId);
        if (!id || !name)
            return; // Skip if no valid coupon data is found

        dataLayerItem.promotion_id = id;
        dataLayerItem.promotion_name = name;
    }

    /**
     * Gets the order type name based on the order type ID and whether it is a curbside order.
     * @param {number | undefined} orderTypeId - The ID of the order type.
     * @param {boolean} isCurbside - Indicates if the order is for curbside pickup.
     * @returns
     */
    private _getOrderTypeName(orderTypeId: number | undefined, isCurbside: boolean): string {
        let orderTypeName = 'not_applicable';
        switch (orderTypeId) {
            case OrderType.DINE_IN:
                orderTypeName = 'in-store';
                break;
            case OrderType.TAKE_OUT:
                if (isCurbside)
                    orderTypeName = 'curbside';
                break;
        }
        return orderTypeName;
    }

    /**
     * Method to get the payment type name containing all applicable payment types for the order.
     * @param {IOrderGiftCard[]} giftCards - The list of gift cards associated with the order.
     * @param {PaymentType} paymentType - The type of payment used for the order.
     * @returns {string} - The name of the payment type.
     */
    private _getPaymentTypeName(giftCards: IOrderGiftCard[], paymentType?: PaymentType): string {
        const paymentTypes = [];
        if (giftCards.length > 0)
            paymentTypes.push("Gift Card");

        switch (paymentType) {
            case PaymentType.PAY_IN_STORE:
                if (giftCards.length === 0)
                    paymentTypes.push("Pay In Store");
                break;
            case PaymentType.CASH:
                if (giftCards.length === 0)
                    paymentTypes.push("Cash");
                break;
            case PaymentType.CREDIT_CARD_MOBILE:
                paymentTypes.push("Credit Card");
                break;
            case PaymentType.CREDIT_CARD:
                paymentTypes.push("Credit Card");
                break;
            case PaymentType.WALLET_CREDIT_CARD:
                paymentTypes.push("Customer Saved Credit Card");
                break;
        }
        return paymentTypes.join(", ");
    }

    /**
     * Sets up an IntersectionObserver to track visibility of a collection of items within a specific element container
     * and fires the specified data layer event when the individual items have at least 80% visibility.
     * @param {HTMLElement} parentDiv - The parent div containing the item cards to observe.
     * @param {string} query - The CSS selector to find specific elements within the parent div.
     * @param {string} itemListName - A unique identifier for the observed item(s).
     * @param { datasetKey: string, handler: Function } options - An object containing the dataset key and handler function to call when items become visible.
     */
    public setupItemsObserver(
        parentDiv: HTMLElement,
        query: string,
        itemListName: string,
        options: { datasetKey: string, handler: Function }
    ): void {
        const self = this;
        const items = Array.from(parentDiv.querySelectorAll(query));
        const visibleSet = new Set<Element>();
        let debounceTimeout: number | undefined;

        function isHTMLElementWithDatasetKey(el: any, key: string): el is HTMLElement {
            return (
                el &&
                el.nodeType === 1 &&
                typeof el.getAttribute === 'function' &&
                'dataset' in el &&
                typeof el.dataset[key] !== 'undefined'
            );
        }

        function buildThresholdArray(steps: number): number[] {
            return Array.from({ length: steps + 1 }, (_, i) => i / steps);
        }

        function processVisibleItems(observer: IntersectionObserver, visibleSet: Set<Element>, entries: IntersectionObserverEntry[]): void {
            const requiredRatio = 0.8;
            entries.forEach(entry => {
                if (entry.intersectionRatio >= requiredRatio) {
                    visibleSet.add(entry.target);
                } else {
                    visibleSet.delete(entry.target);
                }
            });

            clearTimeout(debounceTimeout);
            debounceTimeout = window.setTimeout(() => {
                const ids = [...visibleSet]
                    .filter(el => isHTMLElementWithDatasetKey(el, options.datasetKey))
                    .map(el => Number((el as HTMLElement).dataset[options.datasetKey]!)) ?? [];

                const idIndexes = [...visibleSet]
                    .filter(el => isHTMLElementWithDatasetKey(el, 'index'))
                    .map(el => ({
                        id: Number((el as HTMLElement).dataset[options.datasetKey]!),
                        idx: Number((el as HTMLElement).dataset['index']!)
                    })) ?? [];

                if (ids.length > 0)
                    options.handler.call(self, ids, itemListName, idIndexes);

                visibleSet.forEach(entry => {
                    observer.unobserve(entry);
                });
            }, 150);
        }

        const observer = new IntersectionObserver(
            (entries) => {
                processVisibleItems(observer, visibleSet, entries);
            },
            {
                root: null,
                threshold: buildThresholdArray(20), // For finer tracking between 0-1
            }
        );

        items.forEach(item => observer.observe(item));
    }

    //TODO: Rewrite couponListViewed and couponScrollListeners to use an IntersectionObserver when implementing US 54754
    ///**
    // * Method to trigger recording a "view_promotion" event.
    // */
    //private couponListViewed(): void {
    //    //TODO: Make sure to query new coupon section when it is created in US 54754
    //    const couponList = document.getElementById("div_coupons");
    //    if (!couponList || couponList.classList.contains("hidden"))
    //        return;

    //    const divs = document.querySelectorAll("div.p1.coupon") as NodeListOf<HTMLElement>;
    //    const items: number[] = [];

    //    divs.forEach(div => {
    //        const rect = div.getBoundingClientRect();
    //        const isVisible = (rect.top >= 0);

    //        if (isVisible) {
    //            const visibleHeight = window.innerHeight - rect.top;
    //            const totalHeight = rect.bottom - rect.top;
    //            const visiblePercentage = visibleHeight / totalHeight;

    //            if (visiblePercentage >= 0.7)
    //                items.push(Number(div.dataset.cid));
    //        }
    //    });

    //    if (items.length > 0)
    //        this.view_promotion(items);
    //}

    ///**
    // * Sets up scroll listeners for coupon lists. 
    // * TODO: Should be implemented for future US 54754
    // */
    //public couponScrollListeners(): void {
    //    const self = this;
    //    window.removeEventListener('scroll', self.throttle(self.couponListViewed.bind(self), 250));

    //    setTimeout(() => {
    //        self.couponListViewed();
    //        window.addEventListener('scroll', self.throttle(self.couponListViewed.bind(self), 250));
    //    }, 1000);
    //}

    ///**
    // * Throttles a function to limit the rate at which it can be called.
    // * @param func - The function to throttle.
    // * @param limit - The time limit in milliseconds.
    // * @returns
    // */
    //private throttle(func: (...args: any[]) => void, limit: number): (...args: any[]) => void {
    //    let inThrottle: boolean;

    //    return function (this: any, ...args: any[]): void {
    //        if (!inThrottle) {
    //            func.apply(this, args);
    //            inThrottle = true;
    //            setTimeout(() => inThrottle = false, limit);
    //        }
    //    }
    //}

    /**
     * Method to record a "page_view" event in Google Analytics.
     * @param loginStatus - The login status of the user.
     * @param storeCode - The brand's identifier code of the store.
     * @param user - The user object that viewed the page.
     * @returns
     */
    public page_view(loginStatus: boolean, storeCode: string, user: IUser): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;

        const appPlatform = this._context.getProperty("platform");
        let platform: PlatformType;

        if (!Util.isAppView()) {
            platform = PlatformType.DESKTOP;
        } else if (Util.isiOS(appPlatform)) {
            platform = PlatformType.IOS;
        } else {
            platform = PlatformType.ANDROID;
        }

        const dataLayerObject: IDataLayer_Page_View_Event = {
            event: "page_view",
            platform: platform,
            login_status: loginStatus ? LoginStatus.LOGGEDIN : LoginStatus.LOGGEDOUT,
            store_number: storeCode,
            user_properties: {
                user_id: user.ProfileId ?? 0,
                user_tier: user.LoyaltyData?.TierName ?? '',
                loyalty_program: user.IsLoyalty ?? false,
                lifetime_points: user.LoyaltyData?.EarnedPoints ?? 0
            }
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "sign_in" event in Google Analytics.
     * @param user - The user object that just signed in.
     * @param method - The method of sign-in (e.g., email, social media).
     * @returns
     */
    public sign_in(user: IUser, method: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Sign_In_Event = {
            event: "login",
            method: method,
            user_properties: {
                user_id: user.ProfileId,
                user_tier: user.LoyaltyData?.TierName ?? '',
                loyalty_program: user.IsLoyalty,
                lifetime_points: user.LoyaltyData?.EarnedPoints ?? 0
            }
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "sign_up" event in Google Analytics.
     * @param user - The user object that just signed up.
     * @param loginStatus - The login status of the user.
     * @returns
     */
    public sign_up(user: IUser, loginStatus: boolean): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Sign_Up_Event = {
            event: "sign_up",
            method: 'email',
            login_status: loginStatus ? LoginStatus.LOGGEDIN : LoginStatus.LOGGEDOUT,
            survey_subscriber: user.IsMarketingSurvey ?? false,
            email_subscriber: user.IsMarketingEmail ?? false,
            text_subscriber: user.IsMarketingText ?? false,
            user_properties: {
                user_id: user.ProfileId,
                user_tier: user.LoyaltyData?.TierName ?? '',
                loyalty_program: user.IsLoyalty,
                lifetime_points: user.LoyaltyData?.EarnedPoints ?? 0
            }
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record an "update_profile" event in Google Analytics.
     * @param user - The user object that was updated.
     * @returns
     */
    public update_profile(user: IUser): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Update_Profile_Event = {
            event: "update_profile",
            content_type: ContentType.USER_PROFILE,
            survey_subscriber: user.IsMarketingSurvey ?? false,
            email_subscriber: user.IsMarketingEmail ?? false,
            text_subscriber: user.IsMarketingText ?? false
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "change_password" event in Google Analytics.
     * @param user - The user object that changed the password.
     * @returns
     */
    public change_password(user: IUser): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Profile_Event = {
            event: 'change_password',
            user_properties: this._createUserProperties(user)
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "delete_profile" event in Google Analytics.
     * @param user - The user object that was deleted.
     * @returns
     */
    public delete_profile(user: IUser): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Profile_Event = {
            event: 'delete_profile',
            user_properties: this._createUserProperties(user)
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Creates user properties for Google Analytics.
     * @param user - The user object to prep for the dataLayer push.
     * @returns
     */
    private _createUserProperties(user: IUser): IDataLayer_User_Properties {
        return {
            user_id: user.ProfileId,
            user_tier: user.LoyaltyData?.TierName ?? '',
            loyalty_program: user.IsLoyalty,
            lifetime_points: user.LoyaltyData?.EarnedPoints ?? 0
        };
    }

    /**
     * Method to record a "select_store" event in Google Analytics.
     * @param storeCode - The brand's identifier code of the store that was selected by a user.
     * @returns
     */
    public select_store(storeCode: string, storeName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Select_Store_Event = {
            event: "select_store",
            store_name: storeName,
            store_number: storeCode
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "change_store" event in Google Analytics.
     * @param storeCode - The brand's identifier code of the store that was changed to by a user.
     * @returns
     */
    public change_store(storeCode: string, storeName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Select_Store_Event = {
            event: "change_store",
            store_name: storeName,
            store_number: storeCode
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "menu_click" event in Google Analytics.
     * @param navTree - The navigation tree that was clicked.
     * @param menuType - The type of menu that was clicked.
     * @returns
     */
    public menu_click(navTree: string, menuType: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Menu_Click_Event = {
            event: "nav_click",
            nav_tree: navTree,
            menu_type: menuType
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "start_order_click" event in Google Analytics.
     * @param linkText - The text of the link that was clicked.
     * @param section - The section of the app where the link was clicked.
     * @returns
     */
    public start_order_click(linkText: string, section: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Start_Order_Click_Event = {
            event: 'start_order',
            link_text: linkText,
            section: section
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record an "add_more_items" event in Google Analytics.
     * @param order - The order object associated with the event.
     * @param linkText - The text of the link that was clicked.
     * @param linkUrl - The URL of the link that was clicked.
     * @returns
     */
    public add_more_items(order: Order, linkText: string, linkUrl: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const items = this._get_checkout_items(order, order.getProperty("Items"));
        const dataLayerObject: IDataLayer_Add_More_Items_Event = {
            event: 'add_more_items_CTA_click',
            items: items,
            link_text: linkText,
            link_url: linkUrl
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "customize_item" event in Google Analytics.
     * @param linkText - The text of the link that was clicked.
     * @param linkUrl - The URL of the link that was clicked.
     * @param section - The section of the app where the link was clicked.
     * @returns
     */
    public customize_item(linkText: string, linkUrl: string, section: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Customize_Item_Event = {
            event: 'customize_item',
            link_text: linkText,
            link_url: linkUrl,
            section: section.replace('_', '')
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "customize_option_selected" event in Google Analytics.
     * @param customOptionType - The type of custom option selected (e.g. size, crust, sauce, etc.).
     * @param userSelection - The user's selection.
     * @param section - The section of the app where the option was selected.
     * @returns
     */
    public customize_option_selected(customOptionType: string, userSelection: string, section: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Customize_Option_Selected_Event = {
            event: 'customize_option_selected',
            custom_option_type: customOptionType,
            user_selection: userSelection,
            section: section
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "modal_close" event in Google Analytics.
     * @param contentName - The name of the modal content that was closed.
     * @returns
     */
    public modal_close(contentName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Modal_Close_Event = {
            event: "modal_close",
            content_name: contentName
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record an "apply_coupon_code" event in Google Analytics.
     * @param couponCode - The coupon code that was applied.
     * @param isPromoCoupon - Whether the coupon is a promo coupon or not.
     * @returns
     */
    public apply_coupon_code(couponCode: string, isPromoCoupon: boolean): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Apply_Coupon_Event = {
            event: "apply_coupon",
            coupon: couponCode,
            promo_coupon: isPromoCoupon
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "select_upsell" event in Google Analytics.
     * @param itemName - The name of the item that was upsold.
     * @param widgetDetail - The text of the widget that was upsold.
     * @returns
     */
    public select_upsell(itemName: string, widgetDetail: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Upsell_Selected_Event = {
            event: "select_upsell",
            item_name: itemName,
            widget_detail: widgetDetail
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "rewards_redeem" event in Google Analytics.
     * @param reward - The reward that was redeemed.
     * @param contentType - The type of content that was redeemed.
     * @param storeCode - The brand's identifier code of the store where the reward was redeemed.
     * @returns
     */
    public rewards_redeem(reward: string, contentType: string, storeCode: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Rewards_Redeem_Event = {
            event: "rewards_redeem",
            reward: reward,
            content_type: contentType,
            store_number: storeCode
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "track_order_click" event in Google Analytics.
     * @param transactionId - The ID of the transaction.
     * @param storeCode - The brand's identifier code of the store associated with the transaction.
     * @returns
     */
    public track_order_click(transactionId: number, storeCode: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Track_Order_Click_Event = {
            event: "track_order",
            transaction_id: transactionId,
            store_number: storeCode
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "track_order_connect" event in Google Analytics.
     * @param contentType - The type of content that was connected.
     * @param storeCode - The brand's identifier code of the store associated with the connection.
     * @returns
     */
    public track_order_connect(contentType: string, storeCode: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Track_Order_Connect_Event = {
            event: "track_order_connect",
            content_type: contentType,
            store_number: storeCode
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "re_order_click" event in Google Analytics.
     * @param linkText - The text of the link that was clicked.
     * @param storeCode - The brand's identifier code of the store associated with the click.
     * @returns
     */
    public re_order_click(linkText: string, storeCode: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Re_Order_Click_Event = {
            event: "re_order_click",
            link_text: linkText,
            store_number: storeCode
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "favorite" event in Google Analytics.
     * @param contentType - The type of content that was favorited (e.g. location or item).
     * @param contentName - The name of the content that was favorited.
     * @returns
     */
    public favorite(contentType: string, contentName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Favorite_Event = {
            event: "favorite",
            content_type: contentType,
            content_name: contentName
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record a "remove_favorite" event in Google Analytics.
     * @param contentType - The type of content that was unfavorited (e.g. location or item).
     * @param contentName - The name of the content that was unfavorited.
     * @returns
     */
    public remove_favorite(contentType: string, contentName: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Favorite_Event = {
            event: "remove_favorite",
            content_type: contentType,
            content_name: contentName
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record an "app_download" event in Google Analytics.
     * @param linkText - The text of the link that was clicked.
     * @param linkUrl - The URL of the link that was clicked.
     * @param linkDomain - The domain of the link that was clicked.
     * @param appType - The type of app that was downloaded (e.g. iOS or Android).
     * @returns
     */
    public app_download(linkText: string, linkUrl: string, linkDomain: string, appType: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_App_Download_Event = {
            event: "app_download",
            link_text: linkText,
            link_url: linkUrl,
            link_domain: linkDomain,
            app_type: appType
        }
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }

    /**
     * Method to record an "error_message" event in Google Analytics.
     * @param errorMessage - The error message to be recorded.
     * @param errorType - The type of error (e.g. user, console, etc.).
     * @returns
     */
    public error_message(errorMessage: string, errorType: string): void {
        //@ts-ignore
        if (!globalThis.dataLayer)
            return;
        const dataLayerObject: IDataLayer_Error_Message_Event = {
            event: "error",
            error_message: errorMessage,
            error_type: errorType
        };
        //@ts-ignore
        globalThis.dataLayer.push(dataLayerObject);
    }
}